import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'

import Probability from './Probability'
import truncateFloat from '../common/helpers/truncateFloat'
import { darkGrey, lightGrey, darkRed, lightRed, darkGreen, lightGreen, darkOrange, lightOrange } from './configs/probabilityColors'

const Container = styled.div`
`
const ProbabilitiesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  justify-content: center;
  padding-bottom: 15px;
  border-bottom: 1px solid #dbdbdc;
  margin-top: 10px;
`

export default class Probabilities extends React.Component {
  static propTypes = {
    cashflowRiskRating: PropTypes.number,
    defaultRiskRating: PropTypes.number,
    needsRedecision: PropTypes.bool,
    product: PropTypes.shape({
      lenderKey: PropTypes.string.isRequired,
      finalDecision: PropTypes.string.isRequired
    }).isRequired,
    productUsesDefaultModel: PropTypes.bool.isRequired,
    smartRate: PropTypes.number
  }
  getDefaultColors = ({ percentToThreshold, needsRedecision, productUsesDefaultModel }) => {
    switch (true) {
      case !productUsesDefaultModel:
      case needsRedecision:
        return [darkGrey, lightGrey]
      case  percentToThreshold < 1:
        return [darkGreen, lightGreen]
      case percentToThreshold >= 1 && percentToThreshold <= 1.2:
        return [darkOrange, lightOrange]
      case percentToThreshold > 1.2:
        return [darkRed, lightRed]
      default:
        return [darkGrey, lightGrey]
    }
  }
  getCashflowColors = smartRate => {
    switch (true) {
      case !smartRate:
        return [darkGrey, lightGrey]
      case smartRate > 3:
        return [darkGreen, lightGreen]
      case smartRate === 3:
        return [darkOrange, lightOrange]
      case smartRate < 3:
        return [darkRed, lightRed]
      default:
        return
    }
  }
  getDefaultMessage = ({ percentToThreshold, needsRedecision, defaultThreshold, productUsesDefaultModel }) => {
    const defaultThresholdText = `${parseInt(defaultThreshold * 100, 10)}%`
    switch (true) {
      case !productUsesDefaultModel:
        return 'An R-factor is not available for the selected product.'
      case needsRedecision || !percentToThreshold:
        return 'Please redecision the loan to update the R-factor.'
      case percentToThreshold < .8:
        return 'Great! This loan has a low R-factor.'
      case percentToThreshold >= .8 && percentToThreshold < 1:
        return `Nearing the ${defaultThresholdText} threshold. Review key attributes below.`
      case percentToThreshold >= 1:
        return `Exceeds ${defaultThresholdText} threshold. Review key attributes below.`
      default:
        break
    }
  }
  getDefaultProbability = ({ riskRating, needsRedecision, productUsesDefaultModel }) => {
    if (!productUsesDefaultModel) {
      return ''
    } else if (needsRedecision || productUsesDefaultModel && !riskRating) {
      return '?'
    } else if (!riskRating) {
      return ''
    } else {
      return `${truncateFloat({ float: riskRating * 100, n: 1, truncateZeroes: true })}%`
    }
  }
  getCashflowMessage = smartRate => {
    if (!smartRate) {
      return 'We do not have the required information to predict this.'
    } else {
      return `SmartRate ${smartRate}`
    }
  }
  getCashflowPercent = riskRating => {
    if (!riskRating) {
      return ''
    } else {
      return `${truncateFloat({ float: riskRating * 100, n: 1, truncateZeroes: true })}%`
    }
  }
  render() {
    const { smartRate, cashflowRiskRating, defaultRiskRating, needsRedecision, product, productUsesDefaultModel } = this.props
    const { finalDecision } = product
    const { default_complete_prediction_threshold } = JSON.parse(finalDecision)
    const defaultThreshold = default_complete_prediction_threshold
    const percentToThreshold = parseFloat((defaultRiskRating / defaultThreshold).toFixed(9)) // Compensate for JavaScript rounding errors, ex. for some reason .08/.1 = .79999999999
    const [cashflowPercentColor, cashflowTextColor] = this.getCashflowColors(smartRate)
    const [defaultProbabilityColor, defaultTextColor] = this.getDefaultColors({ percentToThreshold, needsRedecision, productUsesDefaultModel })
    const defaultMessage = this.getDefaultMessage({ percentToThreshold, needsRedecision, defaultThreshold, productUsesDefaultModel })
    return (
      <Container>
        <ProbabilitiesContainer>
          <Probability
            title="Probability of Passing Cash Flow Analysis"
            value={this.getCashflowPercent(cashflowRiskRating)}
            message={this.getCashflowMessage(smartRate)}
            valueColor={cashflowPercentColor}
            messageColor={cashflowTextColor}
            border
          />
          <Probability
            title="R-factor"
            value={this.getDefaultProbability({ riskRating: defaultRiskRating, needsRedecision, productUsesDefaultModel })}
            message={defaultMessage}
            valueColor={defaultProbabilityColor}
            messageColor={defaultTextColor}
          />
        </ProbabilitiesContainer>
      </Container>
    )
  }
}
